import { ofType } from 'redux-observable';
import { concat, of } from 'rxjs';
import { catchError, filter, switchMap } from 'rxjs/operators';

import { CUSTOM_TAG, CUSTOM_TAG_VALUE, TYPE_ALL } from '../../../common/constants/label';
import { DEEP_SEARCH_ENDPOINT_URL } from '../../../common/constants/request';

import { getMainPlayerWidget } from '../../../common/components/Player/helpers';
import { getItemId } from '../../../common/helpers/deepSearch';
import logger from '../../../common/helpers/logger';
import { tmPostRequest } from '../../../common/helpers/request';
import * as commonSelectors from '../../../common/redux/selectors/common';
import * as deepSearchSelectors from '../selectors/deepSearch';
import {
  deepSearchClearResultAction,
  deepSearchFetchErrorAction,
  deepSearchFetchStartedAction,
  deepSearchFetchSuccessAction,
  deepSearchIsAvailableAction,
  deepSearchSetFilterByAction,
  deepSearchSetLoadingStateAction,
  deepSearchSetSelectedTagAction,
  deepSearchSetTargetTagAction,
} from '../slices/deepSearch';

export default (action$, state$) =>
  action$.pipe(
    ofType(deepSearchFetchStartedAction.type),
    filter(({ payload: { query, deepQuery } }) => !!query || !!deepQuery),
    switchMap(({ payload: { videoId, deepQuery, query } }) => {
      const targetTagId = deepSearchSelectors.targetTagId(state$.value);
      const filterBy = deepSearchSelectors.filterBy(state$.value);

      const dsTags = commonSelectors.dsTagsSelector(state$.value);
      const player = commonSelectors.playerSelector(state$.value);
      const publisher = commonSelectors.publisherSelector(state$.value);
      const deepSearch = commonSelectors.deepSearchSelector(state$.value);

      const startTime = Date.now();

      const describeTypes = dsTags
        .filter((item) => deepSearch || item === CUSTOM_TAG_VALUE)
        .map((item) => (item === CUSTOM_TAG_VALUE ? CUSTOM_TAG : item));

      const stream$ = tmPostRequest({
        url: DEEP_SEARCH_ENDPOINT_URL,
        body: {
          clipId: videoId,
          query: query ?? null,
          deepQuery: deepQuery ?? null,
          publisherId: publisher.slug,
          widgetId: player.name,
          passThrough: {
            videoId,
            query: query ?? null,
            deepQuery: deepQuery ?? null,
            compress: true,
          },
          describeTypes: describeTypes.length ? describeTypes : undefined,
        },
      }).pipe(
        switchMap(({ response }) => {
          const tagLogData = response.data ?? [];
          const tagLogDescribe = response.describe ?? [];

          const allowLogs = tagLogData.filter(({ type, time }) => dsTags.filter(Boolean).includes(type) && time !== -1);
          const customTags = tagLogData.filter(({ type }) => type === CUSTOM_TAG_VALUE);

          const videoTags = (
            dsTags.includes(CUSTOM_TAG_VALUE) ? customTags.filter(({ time }) => time === -1) : []
          ).filter(
            ({ value, name }) =>
              !customTags.some((item) => item.name === name && item.value === value && item.time !== -1),
          );

          const allHighlightsIds = allowLogs.reduce((acc, { highlights }) => {
            highlights.forEach((item) => {
              acc[item.id] = true;
            });

            return acc;
          }, {});

          const allowDescribe = tagLogDescribe.filter((item) => allHighlightsIds[item.id]);

          const payload = {
            data: allowLogs.map((item$) => {
              const time = item$.time - (item$.time % 1000);
              const endTime = item$.endTime ? item$.endTime - (item$.endTime % 1000) : null;

              return {
                ...item$,
                name: item$.name,
                value: item$.value,
                color: item$.color ?? undefined,
                time,
                endTime,
                itemId: getItemId(item$.type, item$.value, item$.color, item$.name, time, endTime),
                stopWords: (item$.stopWords ?? []).slice().sort((a, b) => b.length - a.length),
                highlights: item$.highlights.slice().sort((a, b) => b.value.length - a.value.length),
              };
            }),
            describe: allowDescribe.map((item$) => ({
              ...item$,
              disabled: false,
            })),
            videoTags,
          };

          const selectedTag =
            payload.data.find(
              (item) => item.type === filterBy && item.highlights.some((hl) => hl.id === targetTagId),
            ) || payload.data[0];

          const actions = [of(deepSearchSetTargetTagAction(''))];

          if (selectedTag) {
            actions.push(
              of(deepSearchSetFilterByAction(TYPE_ALL)),
              of(deepSearchSetSelectedTagAction(selectedTag.itemId)),
            );
          }

          actions.push(of(deepSearchFetchSuccessAction(payload)), of(deepSearchSetLoadingStateAction(false)));
          const LWP = getMainPlayerWidget();

          LWP.sendStatsPerformDS(query ?? deepQuery, Date.now() - startTime);

          return concat(...actions);
        }),
        catchError((error) => {
          logger.error('modules/Search/redux/epics/deepSearch', error);

          return concat(of(deepSearchSetLoadingStateAction(false)), of(deepSearchFetchErrorAction()));
        }),
      );

      return concat(
        of(deepSearchIsAvailableAction(true)),
        of(deepSearchClearResultAction()),
        of(deepSearchSetLoadingStateAction(true)),
        stream$,
      );
    }),
  );
